import MonthlyAiUsage from "../../components/StitchEditor/MonthlyAiUsage";
import { useEffect, useState, useCallback, useRef, useContext } from "react";
import { useDispatch } from "react-redux";
import { parse, stringify } from "svgson";
import { IonGrid, IonRow, IonCol, IonIcon } from "@ionic/react";
import {
  closeOutline,
  chevronDownOutline,
  globeOutline,
  lockClosedOutline,
  eyeOutline,
  brushOutline,
} from "ionicons/icons";
import { Tooltip, Dropdown, Modal, message } from "antd";
import { setCurrentFileWorkflow } from "../../redux/slices/MyFiles";
import useSvgUpdater from "../../components/SvgEditor/SvgUpdater";
import useEmbroideryMode from "../../components/SvgEditor/hooks/useEmbroideryMode";
import StitchEditor from "../../components/StitchEditor";
import Converter from "../../components/Converter";
import Close from "../../components/SvgEditor/components/Close/Close";
import PrivacyPopup from "../../components/Explore/components/PrivacyPopup";
import SaveIcon from "../../assets/SvgEditor/Save.svg";
import useSaveSVG from "../../hooks/useSaveSvg";
import saveBeforePublish from "./saveBeforePublish";
import { useAuth } from "../../contexts/AuthContext";
import { isEmbroideryTrace } from "../../config/appVariant";
import {
  saveStitchStateToFirestore,
  loadStitchStateFromFirestore,
  saveStitchFileToStorage,
  setPendingConversionInFirestore,
  clearPendingConversionInFirestore,
  normalizeStitchFileId,
  updateFilePublicStatus,
  loadPublicStitchState,
  saveCommunityDesignCopy,
  loadReferenceArtworkForFile,
} from "../../lib/firebase/firebase";
import { updateFilePrivacyInRedux as updateMyFilePrivacy, setShowEditor } from "../../redux/slices/MyFiles";
import {
  updateFilePrivacyInRedux as updateUploadedFilePrivacy,
  addNewlyUploadedFile,
} from "../../redux/slices/UploadedFiles";
import AppContext from "../../contexts/AppContext";
import { sanitizeSvg } from "../../components/SvgEditor/helpers/importSvg";
import { splitSvgLayers } from "../../components/SvgEditor/helpers/svgLayers";
import { REFERENCE_ARTWORK_REGISTRATION_ATTR } from "../../components/SvgEditor/helpers/referenceArtworkRegistration";

const StitchEditorPage = () => {
  const {
    svg,
    svgJoined,
    svgError,
    retryLoad,
    colors,
    setColors,
    updateSvg,
    undo,
    redo,
    changesMade,
    setChangesMade,
    currentFile,
  } = useSvgUpdater();

  const { user, userPreferences, preferencesLoading } = useAuth();
  const dispatch = useDispatch();
  const {
    setSvgStore,
    setImage64,
    setReferenceImage64,
    setImageSource,
    setFileName,
    setPendingEmbroideryMode,
  } = useContext(AppContext);
  const [retraceFlowVisible, setRetraceFlowVisible] = useState(false);
  const [retraceConverterOpen, setRetraceConverterOpen] = useState(false);
  const [retraceLoading, setRetraceLoading] = useState(false);
  const [savedStitchState, setSavedStitchState] = useState(null);
  const [referenceArtwork, setReferenceArtwork] = useState(currentFile?.referenceArtwork || null);

  useEffect(() => {
    let cancelled = false;
    const knownArtwork = savedStitchState?.referenceArtwork || currentFile?.referenceArtwork || null;
    setReferenceArtwork(knownArtwork);

    const restoreArtwork = async () => {
      const restored = await loadReferenceArtworkForFile({
        filePath: currentFile?.filePath || currentFile?.filepath || currentFile?.uri?.fullPath,
        svgMetadataId: currentFile?.svgMetadataId,
        referenceArtwork: knownArtwork,
      });
      if (!cancelled && restored) setReferenceArtwork(restored);
    };

    restoreArtwork();
    return () => {
      cancelled = true;
    };
  }, [
    currentFile?.filePath,
    currentFile?.filepath,
    currentFile?.uri?.fullPath,
    currentFile?.svgMetadataId,
    currentFile?.referenceArtwork,
    savedStitchState?.referenceArtwork,
  ]);

  // Preview of somebody else's community design: rendered from the public copy,
  // and every write below is suppressed until the user saves their own copy.
  // Export still works — the machine file is what they came for — it just isn't
  // persisted to their account. `previewOf` holds the source community file.
  const readOnly = !!currentFile?.readOnly;
  const previewSource = currentFile?.previewOf || null;
  const { loading: savingInProgress } = useSaveSVG(svgJoined, currentFile);
  // Phone-only check — iPad keeps the desktop layout, matching StitchEditor.
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;

  const embroidery = useEmbroideryMode(colors?.length || 0);

  const openRetraceFlow = useCallback(async () => {
    const artwork = referenceArtwork;
    const source = artwork?.dataUrl || artwork?.url;
    if (!source) {
      message.error("The reference artwork is not available for this design.");
      return;
    }

    try {
      let dataUrl = source;
      if (!source.startsWith("data:")) {
        const response = await fetch(source);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const blob = await response.blob();
        dataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = () => reject(reader.error || new Error("Could not read artwork"));
          reader.readAsDataURL(blob);
        });
      }

      setImage64(dataUrl);
      setReferenceImage64?.(dataUrl);
      setImageSource("uploaded");
      setFileName(artwork.name || currentFile?.name || "Source artwork");
      setPendingEmbroideryMode(true);
      setRetraceConverterOpen(false);
      setRetraceFlowVisible(true);
    } catch (error) {
      console.error("Could not open reference artwork for retracing:", error);
      message.error("Could not load the reference artwork.");
    }
  }, [currentFile, referenceArtwork, setImage64, setReferenceImage64, setImageSource, setFileName, setPendingEmbroideryMode]);

  useEffect(() => {
    if (!retraceFlowVisible || retraceConverterOpen) return;
    const timer = setTimeout(() => {
      document.getElementById("AutoConvertUpload")?.click();
    }, 50);
    return () => clearTimeout(timer);
  }, [retraceFlowVisible, retraceConverterOpen]);

  const applyRetracedSvg = useCallback(async (svgString, artworkRegistration, retracedArtwork) => {
    if (!svgString) throw new Error("The converter returned no SVG");
    const parsed = await parse(sanitizeSvg(svgString));
    if (artworkRegistration) {
      parsed.attributes = {
        ...parsed.attributes,
        [REFERENCE_ARTWORK_REGISTRATION_ATTR]: encodeURIComponent(JSON.stringify(artworkRegistration)),
      };
    }
    updateSvg(splitSvgLayers(parsed));
    setChangesMade(true);
    embroidery?.hydrateStitchState?.({
      layerStitchSettings: {},
      layerRails: {},
      layerCutMarks: {},
    });
    // Keep the new bitmap with this private design's saved stitch state.
    // The file catalog may still carry the reference from the initial upload.
    const nextArtwork = retracedArtwork || referenceArtwork;
    setReferenceArtwork(nextArtwork);
    setSavedStitchState({ referenceArtwork: nextArtwork });
    message.success("Artwork retraced. Save to keep the new vector.");
  }, [updateSvg, setChangesMade, embroidery, referenceArtwork]);

  // Auto-enable embroidery mode once SVG is loaded
  useEffect(() => {
    if (svgJoined && embroidery && !embroidery.embroideryMode) {
      embroidery.setEmbroideryMode(true);
    }
  }, [svgJoined, embroidery]);

  // --- Stitch state persistence (mirrors SvgEditorDesktop logic) ---

  const embroideryForAiRef = useRef({ layerStitchSettings: {}, layerRails: {} });
  useEffect(() => {
    embroideryForAiRef.current = {
      layerStitchSettings: embroidery?.layerStitchSettings || {},
      layerRails: embroidery?.layerRails || {},
    };
  }, [embroidery?.layerStitchSettings, embroidery?.layerRails]);

  const stitchAiAnalysisCacheRef = useRef({ fileId: null, analysis: null });
  useEffect(() => {
    const fid = currentFile?.id;
    if (!fid) return;
    if (stitchAiAnalysisCacheRef.current.fileId && stitchAiAnalysisCacheRef.current.fileId !== fid) {
      stitchAiAnalysisCacheRef.current = { fileId: null, analysis: null };
    }
    // Pre-seed the cache with aiAnalysis passed directly from the converter so the
    // Stitch Editor's in-memory cache check hits before Firestore finishes writing.
    if (currentFile?.initialAiAnalysis && !stitchAiAnalysisCacheRef.current.analysis) {
      stitchAiAnalysisCacheRef.current = { fileId: fid, analysis: currentFile.initialAiAnalysis };
    }
  }, [currentFile?.id, currentFile?.initialAiAnalysis]);

  const [stitchStateLoaded, setStitchStateLoaded] = useState(false);

  useEffect(() => {
    if (!currentFile?.id) return;
    if (!readOnly && !user?.uid) return;
    setSavedStitchState(null);
    setStitchStateLoaded(false);
    // A preview inherits the creator's published settings instead of looking in
    // the viewer's own stitchStates, where the synthetic preview id has nothing.
    const load = readOnly
      ? loadPublicStitchState(previewSource?.id)
      : loadStitchStateFromFirestore(
          user.uid,
          currentFile.id,
          currentFile.filepath || currentFile.filePath || currentFile?.uri?.fullPath || null
        );
    load.then((state) => {
      // If Firestore doesn't have aiAnalysis yet (race: converter's saveDstToAccount
      // is async and may not have finished), fall back to the value the converter
      // passed directly on currentFile so the Stitch Editor never re-runs analysis.
      const aiAnalysis = state?.aiAnalysis ?? currentFile?.initialAiAnalysis ?? null;
      const merged = aiAnalysis ? { ...(state || {}), aiAnalysis } : state;
      setSavedStitchState(merged);
      setStitchStateLoaded(true);
      if (merged && embroidery?.hydrateStitchState) embroidery.hydrateStitchState(merged);
    }).catch(() => {
      message.error("Could not load your saved stitches. Please reopen the design to retry.");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFile?.id, user?.uid]);

  const saveStitchToFirestore = useCallback(async () => {
    if (readOnly) return;
    if (!user?.uid || !currentFile?.id || !embroidery?.serializeStitchState) return;
    const state = embroidery.serializeStitchState();
    if (!state) return;
    const existingAi =
      savedStitchState?.aiAnalysis ?? stitchAiAnalysisCacheRef.current?.analysis ?? null;
    const existingFile = savedStitchState?.lastGeneratedFile ?? null;
    await saveStitchStateToFirestore(user.uid, currentFile.id, {
      ...state,
      aiAnalysis: existingAi,
      lastGeneratedFile: existingFile,
      referenceArtwork,
    });
  }, [readOnly, user?.uid, currentFile?.id, embroidery, savedStitchState?.aiAnalysis, savedStitchState?.lastGeneratedFile, referenceArtwork]);

  const onAiAnalysisReady = useCallback(async (aiAnalysis) => {
    if (!aiAnalysis) return;
    // In a preview, keep the analysis for this session only. Writing it would
    // land under the viewer's uid keyed by a design they don't own.
    if (readOnly) {
      setSavedStitchState((prev) => ({ ...prev, aiAnalysis }));
      return;
    }
    if (!user?.uid || !currentFile?.id) return;
    const state = embroidery?.serializeStitchState?.() || {};
    const existingFile = savedStitchState?.lastGeneratedFile ?? null;
    try {
      await saveStitchStateToFirestore(user.uid, currentFile.id, {
        ...state,
        aiAnalysis,
        lastGeneratedFile: existingFile,
      });
    } catch (err) {
      // Non-fatal: keep it in memory for this session even if the write fails.
      console.warn("[StitchEditorPage] persist aiAnalysis failed:", err?.message || err);
    }
    setSavedStitchState((prev) => ({ ...prev, aiAnalysis }));
  }, [readOnly, user?.uid, currentFile?.id, embroidery, savedStitchState?.lastGeneratedFile]);

  const handleStitchFileGenerated = useCallback(
    async ({ format, base64, metadata }) => {
      // Preview: the conversion ran and the user can download it, but nothing is
      // kept. Returning null leaves the "Saved to your account" pill idle, which
      // is honest — there is no account file to save it against yet.
      if (readOnly) return null;
      if (!user?.uid || !currentFile?.id || !format || !base64) return null;
      try {
        const saved = await saveStitchFileToStorage(
          user.uid,
          currentFile.id,
          format,
          base64,
          { metadata },
        );
        const state = embroidery?.serializeStitchState?.() || {};
        const existingAi =
          savedStitchState?.aiAnalysis ?? stitchAiAnalysisCacheRef.current?.analysis ?? null;
        await saveStitchStateToFirestore(user.uid, currentFile.id, {
          ...state,
          aiAnalysis: existingAi,
          lastGeneratedFile: saved,
        });
        setSavedStitchState((prev) => ({ ...(prev || {}), lastGeneratedFile: saved }));
        return saved;
      } catch (err) {
        console.warn("[StitchEditorPage] saveStitchFile failed:", err?.message || err);
        return null;
      }
    },
    [readOnly, user?.uid, currentFile?.id, embroidery, savedStitchState?.aiAnalysis],
  );

  // --- Pending conversion tracking ---
  // Write to Firestore before conversion starts so a refresh can detect and
  // auto-restart it. Clear on success, error, or cancel.
  //
  // IMPORTANT: when an auto-restart fires it goes through this same path,
  // which used to refresh `startedAt` to `Date.now()` — making the 2-hour
  // safety window in `pendingForThisFile` effectively never expire. We now
  // preserve the original `startedAt` if a recent marker already exists, so
  // a chain of refreshes can't keep the loop alive past the safety window.
  const handleConversionStart = useCallback(async (format) => {
    // No marker for a preview: there is nothing to auto-restart into, since a
    // refresh discards the preview entirely.
    if (readOnly) return;
    if (!user?.uid || !currentFile?.id) return;
    // Key by the normalized (extensionless) id so the converter session and
    // the dashboard card (which strips ".svg") read the same entry.
    const pendingKey = `pendingConversion:${normalizeStitchFileId(currentFile.id)}`;
    let startedAt = Date.now();
    try {
      const raw = localStorage.getItem(pendingKey);
      if (raw) {
        const existing = JSON.parse(raw);
        if (existing?.startedAt && Date.now() - existing.startedAt < 2 * 60 * 60 * 1000) {
          startedAt = existing.startedAt;
        }
      }
    } catch (_) { /* fall through with fresh timestamp */ }
    const entry = { format, startedAt };
    // Write to both Firestore (durable) and localStorage (instant, survives
    // Redux wipe on refresh so Card.jsx can re-open the file in Stitch mode).
    try { localStorage.setItem(pendingKey, JSON.stringify(entry)); } catch (_) {}
    await setPendingConversionInFirestore(user.uid, currentFile.id, format, startedAt);
    setSavedStitchState((prev) => ({ ...(prev || {}), pendingConversion: entry }));
  }, [readOnly, user?.uid, currentFile?.id]);

  const handleConversionEnd = useCallback(async () => {
    if (readOnly) return;
    if (!user?.uid || !currentFile?.id) return;
    try { localStorage.removeItem(`pendingConversion:${normalizeStitchFileId(currentFile.id)}`); } catch (_) {}
    // Reset the per-file auto-restart guard so a future Generate click can
    // legitimately auto-recover after a tab close.
    try { sessionStorage.removeItem(`autoRestarted:${currentFile.id}`); } catch (_) {}
    await clearPendingConversionInFirestore(user.uid, currentFile.id);
    setSavedStitchState((prev) => ({ ...(prev || {}), pendingConversion: null }));
  }, [readOnly, user?.uid, currentFile?.id]);

  // Auto-restart a conversion that was interrupted by a page refresh.
  // Only fires once per file load, and only if the pending job is < 2 hours old.
  const autoRestartedRef = useRef(false);
  const pendingConversion = savedStitchState?.pendingConversion;
  const pendingForThisFile =
    pendingConversion?.format &&
    pendingConversion?.startedAt &&
    Date.now() - pendingConversion.startedAt < 2 * 60 * 60 * 1000;

  // --- Layer selection ---
  const [layerSelected, setLayerSelected] = useState("all");

  // --- Path update from cut tool ---
  const handleUpdateLayerPath = useCallback((layerIdx, newPathD) => {
    updateSvg((prevSvg) => {
      if (!prevSvg?.children?.[layerIdx]) return prevSvg;
      const subPaths = newPathD
        ? newPathD.split(/(?=M)/i).filter((s) => s.trim()).map((d) => ({
            name: "path",
            attributes: { d: d.trim(), "fill-rule": "evenodd" },
            children: [],
          }))
        : [];
      const updatedChildren = prevSvg.children.map((child, i) =>
        i === layerIdx
          ? {
              ...child,
              attributes: { ...child.attributes, d: "" },
              children: subPaths,
            }
          : child,
      );
      return { ...prevSvg, children: updatedChildren };
    });
  }, [updateSvg]);

  // --- Top bar close hover state ---
  const [isCloseHovered, setIsCloseHovered] = useState(false);
  const { saveSVG } = useSaveSVG(svgJoined, currentFile);
  const [savingManually, setSavingManually] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const [savedRecently, setSavedRecently] = useState(false);
  // Split so the two can be told apart. A preview asks the visitor to save a
  // copy when they change the design, but resizing for their own hoop is not
  // changing the design — it's how they use it, and prompting for it would fire
  // on almost every preview.
  const [designDirty, setDesignDirty] = useState(false);
  const [exportDirty, setExportDirty] = useState(false);
  const savedTimerRef = useRef(null);
  const stitchHydratedRef = useRef(false);
  const exportHydratedRef = useRef(false);

  useEffect(() => {
    stitchHydratedRef.current = false;
    exportHydratedRef.current = false;
    setDesignDirty(false);
    setExportDirty(false);
    setSavedRecently(false);
    clearTimeout(savedTimerRef.current);
  }, [currentFile?.id]);

  useEffect(() => {
    if (!stitchStateLoaded) return;
    if (!stitchHydratedRef.current) {
      stitchHydratedRef.current = true;
      return;
    }
    setDesignDirty(true);
  }, [
    embroidery?.layerStitchSettings,
    embroidery?.layerRails,
    embroidery?.layerCutMarks,
    embroidery?.layerPieces,
    stitchStateLoaded,
  ]);

  // Export size / hoop are serialized into hoopSettings, so they still need to
  // mark the file dirty for autosave — they just don't count as a design edit.
  useEffect(() => {
    if (!stitchStateLoaded) return;
    if (!exportHydratedRef.current) {
      exportHydratedRef.current = true;
      return;
    }
    setExportDirty(true);
  }, [
    embroidery?.targetWidth,
    embroidery?.targetHeight,
    embroidery?.selectedHoop,
    stitchStateLoaded,
  ]);

  const hasPendingChanges = changesMade || designDirty || exportDirty;
  // What the preview treats as "you've modified this design". Deliberately
  // excludes exportDirty.
  const hasDesignEdits = changesMade || designDirty;
  const isSaving = savingInProgress || savingManually;
  const isSaved = savedRecently && !hasPendingChanges && !isSaving;
  const isPending = hasPendingChanges && !isSaving;

  const handleSave = useCallback(async () => {
    // A preview has no file of its own to save into. Saving here would upload a
    // new file silently, which is exactly the decision we want the user to make
    // explicitly via "Save a copy".
    if (readOnly) return;
    setSaveFailed(false);
    setSavingManually(true);
    try {
      const ok = await saveSVG();
      if (!ok) throw new Error("Artwork could not be saved.");
      if (ok) {
        await saveStitchToFirestore();
        setChangesMade(false);
        setDesignDirty(false);
        setExportDirty(false);
        setSavedRecently(true);
        clearTimeout(savedTimerRef.current);
        savedTimerRef.current = setTimeout(() => setSavedRecently(false), 2500);
        return true;
      }
    } catch (error) {
      setSaveFailed(true);
      message.error("Your changes could not be saved. Keep this design open and press Save to retry.");
      return false;
    } finally {
      setSavingManually(false);
    }
  }, [readOnly, saveSVG, saveStitchToFirestore, setChangesMade]);

  // Autosave: whenever the design has unsaved changes, persist them
  // automatically on a short debounce so the user never has to remember to hit
  // Save. Switching a tool in the top toolbar commits that tool's pending edits
  // (via leaveCutTool → applyAndExit), which marks the design dirty and triggers
  // this — so swapping tools at the top reliably autosaves the committed work.
  const autoSaveTimerRef = useRef(null);
  useEffect(() => {
    if (readOnly) return undefined;
    if (!stitchStateLoaded) return undefined;
    if (!hasPendingChanges || isSaving || saveFailed) return undefined;
    clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      handleSave();
    }, 1000);
    return () => clearTimeout(autoSaveTimerRef.current);
  }, [readOnly, hasPendingChanges, isSaving, saveFailed, stitchStateLoaded, handleSave]);

  // --- Saving a previewed community design into your own account ---

  const [showCopyPrompt, setShowCopyPrompt] = useState(false);
  const [isCopying, setIsCopying] = useState(false);
  const copyPromptedRef = useRef(false);

  useEffect(() => {
    copyPromptedRef.current = false;
    setShowCopyPrompt(false);
  }, [currentFile?.id]);

  const handleSaveCopy = useCallback(async () => {
    if (!readOnly || !previewSource || !svgJoined) return null;
    setIsCopying(true);
    try {
      // Upload what's on screen, not the published original, so anything the
      // user changed while previewing survives into their copy.
      const svgToSave = stringify(svgJoined);
      const svgDataUrl = `data:image/svg+xml;base64,${btoa(
        unescape(encodeURIComponent(svgToSave))
      )}`;

      const state = embroidery?.serializeStitchState?.() || null;
      const aiAnalysis =
        savedStitchState?.aiAnalysis ?? stitchAiAnalysisCacheRef.current?.analysis ?? null;

      const saved = await saveCommunityDesignCopy({
        sourceFile: previewSource,
        svgDataUrl,
        stitchState: state ? { ...state, aiAnalysis, referenceArtwork } : null,
      });

      // Hand the editor its new owned file. Autosave and account saves resume
      // from here because readOnly is absent on this object. Passing svgData
      // keeps SvgUpdater off the network, so the canvas doesn't blank out and
      // reload artwork it already has.
      dispatch(addNewlyUploadedFile(saved));
      dispatch(setShowEditor({ showEditor: true, currentFile: { ...saved, svgData: svgToSave } }));
      setSvgStore({ firebase: saved.uri });
      setShowCopyPrompt(false);
      message.success("Saved to your files — it's yours to edit now.");
      return saved;
    } catch (err) {
      console.warn("[StitchEditorPage] save copy failed:", err?.message || err);
      message.error("Couldn't save a copy. Please try again.");
      return null;
    } finally {
      setIsCopying(false);
    }
  }, [
    readOnly,
    previewSource,
    svgJoined,
    embroidery,
    savedStitchState?.aiAnalysis,
    dispatch,
    setSvgStore,
    referenceArtwork,
  ]);

  // Ask once, the first time they change the design itself. Resizing for a hoop
  // deliberately doesn't count — see hasDesignEdits.
  useEffect(() => {
    if (!readOnly || !hasDesignEdits) return;
    if (copyPromptedRef.current) return;
    copyPromptedRef.current = true;
    setShowCopyPrompt(true);
  }, [readOnly, hasDesignEdits]);

  // --- Community share prompt ---
  //
  // Offered right after a download, because that's the moment the design is
  // finished and the user knows how it turned out. It is strictly an
  // afterthought to the download: StitchEditor calls onDownloaded only once the
  // file is already on disk, and this modal is freely dismissable, so declining
  // or ignoring it never costs the user their file.
  const [showSharePrompt, setShowSharePrompt] = useState(false);
  const [isSharing, setIsSharing] = useState(false);

  // Asked at most once per design, and the answer outlives the session so
  // re-downloading a tweaked design doesn't re-ask.
  const sharePromptKey = currentFile?.id
    ? `sharePrompted:${normalizeStitchFileId(currentFile.id)}`
    : null;

  const markPrompted = useCallback(() => {
    if (!sharePromptKey) return;
    try { localStorage.setItem(sharePromptKey, "1"); } catch (_) { /* private mode */ }
  }, [sharePromptKey]);

  const publishDesign = useCallback(async (sharePublicly) => {
    if (!currentFile?.uri) return;
    await updateFilePublicStatus(currentFile.uri, sharePublicly);
    dispatch(updateMyFilePrivacy({ id: currentFile.id, isPublic: sharePublicly }));
    dispatch(updateUploadedFilePrivacy({ id: currentFile.id, isPublic: sharePublicly }));
  }, [currentFile?.uri, currentFile?.id, dispatch]);

  const handleShareChoice = useCallback(async () => {
    markPrompted();
    setIsSharing(true);
    try {
      // Flush pending edits first: the publish trigger copies what's saved in
      // Firestore and Storage, not what's still sitting in the editor.
      await saveBeforePublish(handleSave, () => publishDesign(true));
      setShowSharePrompt(false);
      message.success("Shared to the community. Thanks for contributing!");
    } catch (err) {
      console.warn("[StitchEditorPage] share failed:", err?.message || err);
      message.error("Couldn't share this design. You can try again from your files.");
    } finally {
      setIsSharing(false);
    }
  }, [markPrompted, handleSave, publishDesign]);

  const dismissSharePrompt = useCallback(() => {
    markPrompted();
    setShowSharePrompt(false);
  }, [markPrompted]);

  // The same publish path as the post-download prompt, driven from the top bar so
  // it can be reversed. Until now the prompt was the only way in and there was no
  // way back out without leaving the editor for the file grid.
  const [privacyUpdating, setPrivacyUpdating] = useState(false);

  // Takes the state to move to rather than flipping the current one, so a second
  // click on the segment that's already lit is a no-op instead of a round trip
  // that undoes the first.
  const setVisibility = useCallback(async (makePublic) => {
    if (!currentFile?.uri || privacyUpdating) return false;
    if (makePublic === !!currentFile.isPublic) return false;
    setPrivacyUpdating(true);
    try {
      // Publishing mirrors what's in Storage and Firestore, not what's still
      // sitting unsaved in the editor, so flush first or the community gets a
      // copy of the design as it was before the last few edits.
      if (makePublic) await saveBeforePublish(handleSave, () => publishDesign(true));
      else await publishDesign(false);
      // Choosing here is an answer to the question the download prompt asks, so
      // don't ask it again for this design.
      markPrompted();
      message.success(
        makePublic
          ? "Now public — anyone can find it in the community."
          : "Now private — removed from the community."
      );
      return true;
    } catch (err) {
      console.warn("[StitchEditorPage] visibility change failed:", err?.message || err);
      message.error("Couldn't update visibility. Try again in a moment.");
      return false;
    } finally {
      setPrivacyUpdating(false);
    }
  }, [
    currentFile?.uri,
    currentFile?.isPublic,
    privacyUpdating,
    handleSave,
    publishDesign,
    markPrompted,
  ]);

  // Opened from the top bar rather than changed there. The control sits next to
  // Save, and a single misplaced click shouldn't be able to publish a design to
  // strangers — so the bar reports the state and the dialog is where it changes.
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  const handlePrivacyChange = useCallback(async (nextIsPublic) => {
    const ok = await setVisibility(nextIsPublic);
    if (ok) setShowPrivacyModal(false);
  }, [setVisibility]);

  const handleDownloaded = useCallback(() => {
    // EmbroideryTrace only. SvgTrace reaches this editor through the SVG/Stitch
    // toggle, but it already prompts for sharing in the SVG editor — prompting
    // again on DST download would just be a second ask for the same design.
    if (!isEmbroideryTrace) return;
    // Never offer to publish a design the user is only previewing — it isn't
    // theirs to share, and it's already public anyway.
    if (readOnly) return;
    if (preferencesLoading) return;
    if (!currentFile?.uri || !sharePromptKey) return;
    if (currentFile?.isPublic) return;

    let alreadyPrompted = false;
    try { alreadyPrompted = localStorage.getItem(sharePromptKey) === "1"; } catch (_) {}
    if (alreadyPrompted) return;

    // Respect the same preference the SVG editor honours, so someone who has
    // already said "always private" is never asked again.
    const defaultPrivacy = userPreferences?.defaultPrivacy || "ask";
    if (defaultPrivacy === "private") {
      markPrompted();
      return;
    }
    if (defaultPrivacy === "public") {
      markPrompted();
      publishDesign(true).catch((err) =>
        console.warn("[StitchEditorPage] auto-share failed:", err?.message || err)
      );
      return;
    }
    setShowSharePrompt(true);
  }, [
    readOnly,
    preferencesLoading,
    currentFile?.uri,
    currentFile?.isPublic,
    sharePromptKey,
    userPreferences?.defaultPrivacy,
    markPrompted,
    publishDesign,
  ]);

  if (!svgJoined && svgError) {
    return (
      <div role="alert" style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16, padding: 24, background: "#f0f0f0" }}>
        <h2>We couldn’t open the stitch editor</h2>
        <p style={{ maxWidth: 480, textAlign: "center" }}>{svgError}</p>
        <div style={{ display: "flex", gap: 16 }}>
          <button type="button" onClick={retryLoad}>Try again</button>
          <button type="button" onClick={() => dispatch(setShowEditor({ showEditor: false, currentFile: "" }))}>Back to files</button>
        </div>
      </div>
    );
  }

  if (!svgJoined) {
    return (
      <div
        style={{
          width: "100%",
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          background: "#f0f0f0",
          fontFamily: "Clash Grotesk, sans-serif",
        }}
      >
        {/* Top bar placeholder — matches .svgtrace-topbar gradient, safe area, and height */}
        <div
          style={{
            height: "calc(36px + env(safe-area-inset-top, 0px))",
            minHeight: "calc(36px + env(safe-area-inset-top, 0px))",
            maxHeight: "calc(36px + env(safe-area-inset-top, 0px))",
            marginTop: "calc(-1 * env(safe-area-inset-top, 0px))",
            paddingTop: "env(safe-area-inset-top, 0px)",
            background: "linear-gradient(135deg, #1e293b 0%, #334155 100%)",
            borderBottom: "1px solid rgba(6, 182, 212, 0.2)",
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
            flexShrink: 0,
          }}
        />

        {/* Canvas area with centered spinner pill */}
        <div
          style={{
            flex: 1,
            background: "#f0f0f0",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "6px 14px 6px 10px",
              borderRadius: "20px",
              background: "rgba(30, 30, 30, 0.72)",
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
              boxShadow: "0 2px 12px rgba(0, 0, 0, 0.18)",
              color: "rgba(255, 255, 255, 0.92)",
              fontSize: "11px",
              fontWeight: 500,
              letterSpacing: "0.02em",
            }}
          >
            <div
              style={{
                width: "14px",
                height: "14px",
                border: "2px solid rgba(255, 255, 255, 0.25)",
                borderTopColor: "rgba(244, 83, 196, 0.9)",
                borderRadius: "50%",
                animation: "stitchEditorSpin 0.7s linear infinite",
                flexShrink: 0,
              }}
            />
            <span>Loading stitch editor…</span>
          </div>
        </div>

        <style>
          {`@keyframes stitchEditorSpin { to { transform: rotate(360deg); } }`}
        </style>
      </div>
    );
  }

  return (
    <div data-tutorial="stitch-editor" style={{ width: "100%", height: "100vh", display: "flex", flexDirection: "column", background: "#1a1a2e" }}>
      {/* Top bar */}
      <IonGrid className="svgtrace-topbar">
        <IonRow className="svgtrace-topbar-row">
          {/* Left: Close + Save */}
          <IonCol size="auto" className="svgtrace-topbar-left-actions">
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <Close
                changesMade={readOnly ? hasDesignEdits : hasPendingChanges}
                svgJoined={svgJoined}
                currentFile={currentFile}
                onStitchSave={saveStitchToFirestore}
                saveOverride={readOnly ? handleSaveCopy : undefined}
                saveLabel={readOnly ? "Save a copy" : "Save Changes"}
                styles={{
                  height: "26px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "4px",
                  background: isCloseHovered ? "#fee2e2" : "rgba(255,255,255,0.12)",
                  borderRadius: "6px",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  border: isCloseHovered ? "1px solid #fca5a5" : "1px solid rgba(255,255,255,0.18)",
                  padding: "0 8px",
                }}
                setIsCloseHovered={setIsCloseHovered}
              >
                <IonIcon
                  icon={closeOutline}
                  style={{
                    width: "14px",
                    height: "14px",
                    color: isCloseHovered ? "#dc2626" : "rgba(255,255,255,0.9)",
                    transition: "color 0.2s ease",
                    flexShrink: 0,
                  }}
                />
                <span style={{
                  fontSize: "11px",
                  fontWeight: 600,
                  fontFamily: "Clash Grotesk",
                  color: isCloseHovered ? "#dc2626" : "rgba(255,255,255,0.9)",
                  transition: "color 0.2s ease",
                  lineHeight: 1,
                }}>
                  Close
                </span>
              </Close>
              {readOnly && (
                <Tooltip
                  title="Keep this design in your files, with anything you've changed"
                  placement="bottom"
                >
                  <button
                    onClick={handleSaveCopy}
                    disabled={isCopying}
                    style={{
                      fontWeight: 600,
                      fontFamily: "Clash Grotesk",
                      padding: "3px 10px",
                      borderRadius: "6px",
                      fontSize: "11px",
                      whiteSpace: "nowrap",
                      height: "26px",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      transition: "all 0.2s ease",
                      color: "#fff",
                      background: isCopying
                        ? "rgba(244,83,196,0.5)"
                        : "linear-gradient(135deg,#F453C4 0%,#c7429f 100%)",
                      border: "none",
                      cursor: isCopying ? "wait" : "pointer",
                    }}
                  >
                    <img
                      src={SaveIcon}
                      alt="save"
                      style={{ width: "11px", height: "11px", filter: "brightness(0) invert(1)" }}
                    />
                    {isCopying ? "Saving…" : "Save a copy"}
                  </button>
                </Tooltip>
              )}
              {!readOnly && (() => {
                const btnStyle = isSaved
                  ? { color: "#16a34a", background: "rgba(22,163,74,0.12)", border: "1px solid rgba(22,163,74,0.45)", cursor: "default" }
                  : isPending
                  ? { color: "#fbbf24", background: "rgba(251,191,36,0.12)", border: "1px solid rgba(251,191,36,0.55)", cursor: "pointer" }
                  : isSaving
                  ? { color: "rgba(255,255,255,0.5)", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", cursor: "wait" }
                  : { color: "rgba(255,255,255,0.35)", background: "transparent", border: "1px solid rgba(255,255,255,0.12)", cursor: "default" };

                const label = isSaved ? "Saved ✓" : isSaving ? "Saving…" : "Save";
                const iconFilter = isSaved
                  ? "brightness(0) saturate(100%) invert(48%) sepia(98%) saturate(400%) hue-rotate(95deg) brightness(95%) contrast(90%)"
                  : isPending
                  ? "brightness(0) saturate(100%) invert(78%) sepia(60%) saturate(600%) hue-rotate(5deg) brightness(103%) contrast(96%)"
                  : "brightness(0) invert(1) opacity(0.35)";

                const tooltip = isPending
                  ? "Unsaved changes — click to save"
                  : isSaving
                  ? "Saving…"
                  : isSaved
                  ? "All changes saved"
                  : "No unsaved changes";

                return (
                  <Tooltip title={tooltip} placement="bottom">
                    <button
                      style={{
                        fontWeight: 600,
                        fontFamily: "Clash Grotesk",
                        padding: "3px 10px",
                        borderRadius: "6px",
                        fontSize: "11px",
                        whiteSpace: "nowrap",
                        height: "26px",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        transition: "all 0.2s ease",
                        ...btnStyle,
                      }}
                      disabled={!isPending || isSaving}
                      onClick={isPending ? handleSave : undefined}
                      onMouseEnter={(e) => {
                        if (isPending) e.currentTarget.style.background = "rgba(251,191,36,0.22)";
                      }}
                      onMouseLeave={(e) => {
                        if (isPending) e.currentTarget.style.background = "rgba(251,191,36,0.12)";
                      }}
                    >
                      {isPending && !isSaving && (
                        <span style={{
                          width: "6px",
                          height: "6px",
                          borderRadius: "50%",
                          background: "#fbbf24",
                          flexShrink: 0,
                          boxShadow: "0 0 0 2px rgba(251,191,36,0.25)",
                        }} />
                      )}
                      <img
                        src={SaveIcon}
                        alt="save"
                        style={{ width: "11px", height: "11px", filter: iconFilter, transition: "filter 0.2s ease" }}
                      />
                      {label}
                    </button>
                  </Tooltip>
                );
              })()}

              {/* Only for a design the user owns and that exists in Storage —
                  publishing works off the file's path, so there's nothing to
                  publish until the first save has happened. */}
              {!readOnly && isEmbroideryTrace && currentFile?.uri && (() => {
                const isPublic = !!currentFile.isPublic;

                return (
                  <>
                    {/* Separates reporting from doing. Save and Close act on the
                        file; this reports a property of it and opens a dialog. */}
                    <div style={{
                      width: "1px",
                      height: "18px",
                      background: "rgba(255,255,255,0.14)",
                      flexShrink: 0,
                      margin: "0 2px",
                    }} />
                    <Tooltip title="Change who can see this design" placement="bottom">
                      <button
                        type="button"
                        className="stitch-visibility-chip"
                        data-public={isPublic ? "true" : "false"}
                        onClick={() => setShowPrivacyModal(true)}
                      >
                        <IonIcon
                          icon={isPublic ? globeOutline : lockClosedOutline}
                          style={{ fontSize: "12px", flexShrink: 0 }}
                        />
                        <span className="svgtrace-topbar-visibility-label">
                          {isPublic ? "Public" : "Private"}
                        </span>
                      </button>
                    </Tooltip>
                  </>
                );
              })()}
            </div>
          </IonCol>

          {/* Center: Mode Toggle or Static Label */}
          <IonCol className="svgtrace-topbar-filename-section">
            <div className="svgtrace-topbar-center">
              {isEmbroideryTrace ? (
                <span style={{ fontSize: "11px", fontWeight: 600, fontFamily: "Clash Grotesk", color: "rgba(255,255,255,0.9)" }}>
                  Stitch Editor
                </span>
              ) : isMobile ? (
                <Dropdown
                  menu={{
                    items: [
                      { key: "vector", label: "SVG Editor" },
                      { key: "stitch", label: "Stitch Editor" },
                    ],
                    selectedKeys: ["stitch"],
                    onClick: ({ key }) => dispatch(setCurrentFileWorkflow(key)),
                  }}
                  trigger={["click"]}
                >
                  <button style={{
                    display: "flex", alignItems: "center", gap: "4px",
                    background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.15)",
                    borderRadius: "6px", padding: "3px 10px", cursor: "pointer",
                    color: "rgba(255,255,255,0.9)", fontSize: "11px", fontWeight: 600,
                    fontFamily: "Clash Grotesk",
                  }}>
                    Stitch Editor
                    <IonIcon icon={chevronDownOutline} style={{ fontSize: "10px", opacity: 0.7 }} />
                  </button>
                </Dropdown>
              ) : (
                <div style={{ display: "flex", alignItems: "center", gap: "2px", background: "rgba(255,255,255,0.06)", borderRadius: "6px", padding: "2px" }}>
                  <button
                    onClick={() => dispatch(setCurrentFileWorkflow("vector"))}
                    style={{
                      fontSize: "10px", fontWeight: 600, fontFamily: "Clash Grotesk",
                      padding: "3px 10px", borderRadius: "4px", cursor: "pointer", border: "none",
                      background: "transparent",
                      color: "rgba(255,255,255,0.5)",
                      transition: "all 0.15s ease",
                    }}
                  >
                    SVG
                  </button>
                  <button
                    style={{
                      fontSize: "10px", fontWeight: 600, fontFamily: "Clash Grotesk",
                      padding: "3px 10px", borderRadius: "4px", cursor: "pointer", border: "none",
                      background: "rgba(255,255,255,0.15)",
                      color: "#fff",
                      transition: "all 0.15s ease",
                    }}
                  >
                    Stitch
                  </button>
                </div>
              )}
            </div>
          </IonCol>

          {/* Right: Export slot (portal target for StitchExportButton) */}
          <IonCol size="auto" className="svgtrace-topbar-right-section">
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {isEmbroideryTrace && <MonthlyAiUsage placement="topbar" />}
            <div id="stitch-topbar-export-slot" style={{ display: "flex", alignItems: "center" }} />
            </div>
          </IonCol>
        </IonRow>
      </IonGrid>

      {/* Preview strip — states plainly that nothing here is being saved, so
          the "Save a copy" button in the top bar has an obvious purpose. */}
      {readOnly && (
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
          flexShrink: 0,
          padding: "5px 12px",
          background: "rgba(244,83,196,0.1)",
          borderBottom: "1px solid rgba(244,83,196,0.25)",
          color: "rgba(255,255,255,0.85)",
          fontSize: "11px",
          fontWeight: 500,
          fontFamily: "Clash Grotesk, sans-serif",
        }}>
          <IonIcon icon={eyeOutline} style={{ fontSize: "13px", flexShrink: 0 }} />
          <span>
            Previewing a community design. Resize and export freely — changes
            aren't saved unless you save a copy.
          </span>
        </div>
      )}

      {/* Stitch Editor body */}
      <div style={{
        position: "relative",
        width: "100%",
        height: readOnly ? "calc(100vh - 36px - 27px)" : "calc(100vh - 36px)",
        overflow: "hidden",
      }}>
        <StitchEditor
          svgJoined={svgJoined}
          svgSplit={svg}
          colors={colors}
          setColors={setColors}
          updateSvg={updateSvg}
          embroidery={embroidery}
          fileName={currentFile?.name}
          fileId={currentFile?.id}
          stitchAiAnalysisCacheRef={stitchAiAnalysisCacheRef}
          embroideryForAiRef={embroideryForAiRef}
          savedStitchState={savedStitchState}
          stitchStateLoaded={stitchStateLoaded}
          initialAiAnalysis={currentFile?.initialAiAnalysis}
          initialDstBase64={currentFile?.initialDstBase64}
          referenceArtwork={referenceArtwork}
          readOnly={readOnly}
          onAiAnalysisReady={onAiAnalysisReady}
          onStitchFileGenerated={handleStitchFileGenerated}
          onConversionStart={handleConversionStart}
          onConversionEnd={handleConversionEnd}
          pendingConversion={pendingForThisFile ? pendingConversion : null}
          autoRestartedRef={autoRestartedRef}
          layerSelected={layerSelected}
          setLayerSelected={setLayerSelected}
          onUpdateLayerPath={handleUpdateLayerPath}
          onSvgUndo={undo}
          onSvgRedo={redo}
          onDownloaded={handleDownloaded}
          onRetraceArtwork={openRetraceFlow}
        />
      </div>

      {retraceFlowVisible && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Retrace artwork"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1000,
            background: "rgba(15,23,42,0.58)",
            backdropFilter: "blur(5px)",
          }}
        >
          {!retraceConverterOpen && (
            <div style={{
              position: "absolute", inset: 0, display: "flex", alignItems: "center",
              justifyContent: "center", color: "white", fontFamily: "Clash Grotesk, sans-serif",
              fontSize: "14px", fontWeight: 600,
            }}>
              Preparing reference artwork…
            </div>
          )}
          <Converter
            loadingConverter={retraceLoading}
            setLoadingConverter={setRetraceLoading}
            setLoadingMessage={() => {}}
            converterOpen={retraceConverterOpen}
            setConverterOpen={setRetraceConverterOpen}
            onRetraceComplete={applyRetracedSvg}
            onFlowClose={() => setRetraceFlowVisible(false)}
          />
        </div>
      )}

      {/* The same dialog the file grid opens, so privacy looks and behaves the
          same wherever it's reached from. It stages the change and only applies
          it on Save Changes. */}
      {!readOnly && isEmbroideryTrace && currentFile?.uri && (
        <PrivacyPopup
          isOpen={showPrivacyModal}
          onClose={() => setShowPrivacyModal(false)}
          file={currentFile}
          onPrivacyChange={handlePrivacyChange}
          isUpdating={privacyUpdating}
        />
      )}

      {/* Offered the first time a previewed design is actually modified */}
      <Modal
        title={null}
        open={showCopyPrompt}
        onCancel={() => setShowCopyPrompt(false)}
        footer={null}
        centered
        width={400}
        className="sharing-modal"
        destroyOnClose
      >
        <div style={{ textAlign: "center", padding: "16px 8px 4px" }}>
          <div style={{ fontSize: "24px", marginBottom: "12px" }}>✏️</div>
          <h2 style={{ margin: "0 0 8px 0", fontSize: "19px", fontWeight: 600 }}>
            Save this to your files?
          </h2>
          <p style={{ color: "#666", margin: "0 0 20px 0", fontSize: "13px", lineHeight: 1.5 }}>
            You're editing a community design, so your changes aren't being
            saved. Save a copy and it becomes yours — the original creator stays
            credited.
          </p>

          <button
            onClick={handleSaveCopy}
            disabled={isCopying}
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              padding: "11px 16px",
              borderRadius: "10px",
              border: "none",
              background: isCopying ? "#f9a8d4" : "#F453C4",
              color: "#fff",
              fontSize: "14px",
              fontWeight: 600,
              fontFamily: "Clash Grotesk, sans-serif",
              cursor: isCopying ? "wait" : "pointer",
              marginBottom: "8px",
            }}
          >
            <IonIcon icon={brushOutline} style={{ fontSize: "16px" }} />
            {isCopying ? "Saving…" : "Save a copy"}
          </button>

          <button
            onClick={() => setShowCopyPrompt(false)}
            disabled={isCopying}
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              padding: "11px 16px",
              borderRadius: "10px",
              border: "1px solid #d9d9d9",
              background: "#fff",
              color: "#666",
              fontSize: "14px",
              fontWeight: 600,
              fontFamily: "Clash Grotesk, sans-serif",
              cursor: isCopying ? "wait" : "pointer",
            }}
          >
            Keep previewing
          </button>

          <p style={{ fontSize: "11px", color: "#999", margin: "14px 0 0" }}>
            You can still export a stitch file without saving — you just won't
            keep the design or your edits.
          </p>
        </div>
      </Modal>

      {/* Post-download community share prompt */}
      <Modal
        title={null}
        open={showSharePrompt}
        onCancel={dismissSharePrompt}
        footer={null}
        centered
        width={400}
        className="sharing-modal"
        destroyOnClose
      >
        <div style={{ textAlign: "center", padding: "16px 8px 4px" }}>
          <div style={{ fontSize: "24px", marginBottom: "12px" }}>🧵</div>
          <h2 style={{ margin: "0 0 8px 0", fontSize: "19px", fontWeight: 600 }}>
            Share this design?
          </h2>
          <p style={{ color: "#666", margin: "0 0 20px 0", fontSize: "13px", lineHeight: 1.5 }}>
            Your file is downloaded and ready to stitch. Add it to the community
            so others can open it in the editor and resize it for their own hoop
            and fabric.
          </p>

          <button
            onClick={handleShareChoice}
            disabled={isSharing}
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              padding: "11px 16px",
              borderRadius: "10px",
              border: "none",
              background: isSharing ? "#93c5fd" : "#1890ff",
              color: "#fff",
              fontSize: "14px",
              fontWeight: 600,
              fontFamily: "Clash Grotesk, sans-serif",
              cursor: isSharing ? "wait" : "pointer",
              marginBottom: "8px",
            }}
          >
            <IonIcon icon={globeOutline} style={{ fontSize: "16px" }} />
            {isSharing ? "Sharing…" : "Share to community"}
          </button>

          <button
            onClick={dismissSharePrompt}
            disabled={isSharing}
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              padding: "11px 16px",
              borderRadius: "10px",
              border: "1px solid #d9d9d9",
              background: "#fff",
              color: "#666",
              fontSize: "14px",
              fontWeight: 600,
              fontFamily: "Clash Grotesk, sans-serif",
              cursor: isSharing ? "wait" : "pointer",
            }}
          >
            <IonIcon icon={lockClosedOutline} style={{ fontSize: "16px" }} />
            Keep private
          </button>

          <p style={{ fontSize: "11px", color: "#999", margin: "14px 0 0" }}>
            Your machine file stays yours — only the design is shared. You can
            change this anytime from your files.
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default StitchEditorPage;
